import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Loader2, RefreshCw, TrendingDown, TrendingUp } from "lucide-react";
import { billsRead, isFeatureOff } from "@/lib/billsNative";

const money = (v, c = "INR") =>
  v == null ? "—" : `${c === "INR" ? "₹" : `${c} `}${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const Stat = ({ label, value, testId }) => (
  <div data-testid={testId}>
    <dt className="text-[12px] text-archival-muted">{label}</dt>
    <dd className="text-[15px] font-medium text-archival-primary tabular-nums mt-0.5">{value}</dd>
  </div>
);

export default function VendorDetail() {
  const { vendorId } = useParams();
  const [state, setState] = useState({ phase: "loading", data: null });
  const [openHistory, setOpenHistory] = useState(null);
  const [compare, setCompare] = useState({ id: null, loading: false, data: null, error: false });

  const toggleHistory = (id) => setOpenHistory((cur) => (cur === id ? null : id));
  const loadCompare = useCallback(async (id) => {
    setCompare({ id, loading: true, data: null, error: false });
    const r = await billsRead.ingredientPrices(id, {});
    setCompare({ id, loading: false, data: r.ok ? r.data : null, error: !r.ok });
  }, []);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, phase: s.data ? "refreshing" : "loading" }));
    const r = await billsRead.vendorDetail(vendorId);
    if (isFeatureOff(r) || (!r.ok && (r.status === 503 || r.status === 0)))
      return setState({ phase: "unavailable", data: null });
    if (r.status === 404) return setState({ phase: "not-found", data: null });
    if (!r.ok) return setState({ phase: "error", data: null });
    setState({ phase: "ready", data: r.data });
  }, [vendorId]);

  useEffect(() => { load(); }, [load]);

  const back = (
    <Link to="/vendors" data-testid="vendor-detail-back"
      className="inline-flex items-center gap-1.5 text-[13px] text-archival-secondary hover:text-archival-primary transition-colors">
      <ArrowLeft size={14} /> All vendors
    </Link>
  );

  if (state.phase === "loading")
    return (
      <div className="space-y-6">
        {back}
        <div className="flex items-center gap-2 text-archival-secondary text-[14px]" data-testid="vendor-detail-loading">
          <Loader2 className="animate-spin" size={16} /> Loading vendor…
        </div>
      </div>
    );

  if (state.phase === "not-found")
    return (
      <div className="space-y-4">
        {back}
        <p className="text-[15px] text-archival-secondary" data-testid="vendor-detail-missing">
          No purchase history for this vendor in your outlets.
        </p>
      </div>
    );

  if (state.phase !== "ready" && state.phase !== "refreshing")
    return (
      <div className="space-y-6">
        {back}
        <div className="rounded-md border border-amber-200 bg-amber-50 p-6 max-w-xl" data-testid="vendor-detail-unavailable">
          <div className="flex items-center gap-2 text-amber-900">
            <AlertTriangle size={16} />
            <h2 className="text-base font-semibold">Vendor history is temporarily unavailable</h2>
          </div>
          <p className="mt-2 text-[13px] text-amber-900/90">
            We couldn&apos;t reach the Bills datastore. Nothing has been changed.
          </p>
          <button type="button" onClick={load} data-testid="vendor-detail-retry"
            className="mt-4 inline-flex items-center gap-1.5 rounded px-3 py-2 text-[13px] font-medium border border-amber-300 hover:bg-amber-100 transition-colors">
            <RefreshCw size={13} /> Retry
          </button>
        </div>
      </div>
    );

  const d = state.data;
  const s = d.summary;

  return (
    <div className="space-y-10 pb-16" data-testid="vendor-detail">
      {back}

      <header className="space-y-1">
        <h1 className="text-3xl sm:text-4xl font-semibold text-archival-primary tracking-tight leading-tight">
          {d.vendor_name}
        </h1>
        <p className="text-[13px] text-archival-muted">
          {d.gstin ? `GSTIN ${d.gstin}` : "No GSTIN on file"} · {d.vendor_id}
        </p>
      </header>

      <dl className="grid grid-cols-2 lg:grid-cols-5 gap-5 rounded-md border border-archival bg-archival-surface p-5">
        <Stat label="Purchase value" value={money(s.purchase_value, s.currency)} testId="vendor-stat-value" />
        <Stat label="Confirmed bills" value={s.confirmed_bills} testId="vendor-stat-confirmed" />
        <Stat label="All bills" value={s.total_bills} testId="vendor-stat-total" />
        <Stat label="First purchase" value={s.first_purchase || "—"} testId="vendor-stat-first" />
        <Stat label="Last purchase" value={s.last_purchase || "—"} testId="vendor-stat-last" />
      </dl>

      <section className="space-y-3" data-testid="vendor-items">
        <h2 className="text-base md:text-lg font-semibold text-archival-primary tracking-tight">Items purchased</h2>
        {d.items.length === 0 ? (
          <p className="text-[13px] text-archival-muted" data-testid="vendor-items-empty">
            No mapped items yet. Items appear once bills from this vendor are confirmed.
          </p>
        ) : (
          <ul className="divide-y divide-archival rounded-md border border-archival bg-archival-surface overflow-hidden"
            data-testid="vendor-items-list">
            {d.items.map((it) => {
              const up = it.price_movement_percent != null && it.price_movement_percent > 0;
              return (
                <li key={it.ingredient_id} className="px-4 py-3" data-testid={`vendor-item-${it.ingredient_id}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                    <p className="text-[14px] font-medium text-archival-primary">{it.ingredient_name}</p>
                    <p className="text-[13px] text-archival-primary tabular-nums">
                      {it.last_unit_price != null
                        ? `${money(it.last_unit_price, s.currency)} / ${it.last_unit || "unit"}`
                        : "Price not recorded"}
                      {it.price_movement_percent != null && (
                        <span className={`ml-2 inline-flex items-center gap-0.5 text-[12px] font-medium ${up ? "text-red-700" : "text-emerald-700"}`}
                          data-testid={`vendor-item-move-${it.ingredient_id}`}>
                          {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                          {up ? "+" : ""}{it.price_movement_percent}%
                        </span>
                      )}
                    </p>
                  </div>
                  <p className="text-[12px] text-archival-secondary mt-0.5">
                    Last bought {it.last_purchase_date || "—"}
                    {it.last_quantity != null ? ` · ${it.last_quantity} ${it.last_unit || ""}` : ""}
                    {` · ${it.purchases} ${it.purchases === 1 ? "purchase" : "purchases"}`}
                    {it.previous_unit_price != null
                      ? ` · previously ${money(it.previous_unit_price, s.currency)}`
                      : ""}
                  </p>
                  {it.price_movement_percent == null && it.price_comparison_unavailable_reason && (
                    <p className="text-[11px] text-archival-muted mt-0.5"
                      data-testid={`vendor-item-nocompare-${it.ingredient_id}`}>
                      {it.price_comparison_unavailable_reason}
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <button type="button" onClick={() => toggleHistory(it.ingredient_id)}
                      data-testid={`vendor-item-history-btn-${it.ingredient_id}`}
                      className="text-[12px] underline text-archival-secondary">
                      {openHistory === it.ingredient_id ? "Hide price history" : "Price history"}
                    </button>
                    <button type="button" onClick={() => loadCompare(it.ingredient_id)}
                      data-testid={`vendor-item-compare-btn-${it.ingredient_id}`}
                      className="text-[12px] underline text-archival-secondary">
                      Compare vendors
                    </button>
                  </div>

                  {openHistory === it.ingredient_id && (
                    <table className="mt-2 w-full text-[12px]" data-testid={`vendor-item-history-${it.ingredient_id}`}>
                      <thead>
                        <tr className="text-archival-muted text-left">
                          <th className="font-normal py-1">Date</th>
                          <th className="font-normal py-1">Canonical quantity</th>
                          <th className="font-normal py-1 text-right">Price / canonical unit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(it.history || []).map((h) => (
                          <tr key={h.bill_id} className="border-t border-archival">
                            <td className="py-1">{h.date || "—"}</td>
                            <td className="py-1">
                              {h.canonical_quantity != null ? `${h.canonical_quantity} ${h.canonical_unit || ""}` : "—"}
                            </td>
                            <td className="py-1 text-right tabular-nums">
                              {h.comparable
                                ? `${money(h.canonical_unit_price, s.currency)} / ${h.canonical_unit}`
                                : "not comparable"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {compare.id === it.ingredient_id && (
                    <div className="mt-2 rounded border border-archival bg-archival-soft/40 p-2"
                      data-testid={`vendor-compare-${it.ingredient_id}`}>
                      {compare.loading ? (
                        <p className="text-[12px] text-archival-muted">Comparing vendors…</p>
                      ) : compare.error ? (
                        <p className="text-[12px] text-archival-muted">Comparison unavailable right now.</p>
                      ) : compare.data?.mixed_units ? (
                        <p className="text-[12px] text-archival-muted">
                          Price comparison unavailable — units are not comparable across these vendors.
                        </p>
                      ) : (
                        <>
                          <p className="text-[12px] font-medium text-archival-primary mb-1">
                            {compare.data?.ingredient_name} — price per {compare.data?.comparable_unit || "canonical unit"}
                          </p>
                          <ul className="space-y-1">
                            {(compare.data?.vendors || []).map((v) => (
                              <li key={v.vendor_id} className="flex items-baseline justify-between gap-3 text-[12px]">
                                <span className="truncate">{v.vendor_name}</span>
                                <span className="tabular-nums text-archival-secondary shrink-0">
                                  {v.latest_price != null
                                    ? `${money(v.latest_price, s.currency)}${v.previous_price != null ? ` (prev ${money(v.previous_price, s.currency)})` : ""}`
                                    : "no comparable price"}
                                </span>
                              </li>
                            ))}
                          </ul>
                          <p className="text-[11px] text-archival-muted mt-1">
                            Canonical equivalent units only. The lowest price is not necessarily the best supplier.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        <p className="text-[11px] text-archival-muted">
          Prices come from confirmed bills only — never from unreviewed OCR values.
        </p>
      </section>

      <section className="space-y-3" data-testid="vendor-bills">
        <h2 className="text-base md:text-lg font-semibold text-archival-primary tracking-tight">Bills</h2>
        <ul className="divide-y divide-archival rounded-md border border-archival bg-archival-surface overflow-hidden">
          {d.bills.map((b) => (
            <li key={b.bill_id}>
              <Link to={`/bills/detail/${b.bill_id}`} data-testid={`vendor-bill-${b.bill_id}`}
                className="flex items-center gap-4 px-4 py-3 hover:bg-archival-base transition-colors">
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-medium text-archival-primary truncate">
                    {b.bill_number || "No bill number"}
                  </p>
                  <p className="text-[12px] text-archival-secondary">{b.bill_date || "—"}</p>
                </div>
                <span className="text-[14px] font-medium text-archival-primary tabular-nums shrink-0">
                  {money(b.total_amount, b.currency || s.currency)}
                </span>
                <span className="text-[11px] uppercase tracking-wide text-archival-muted shrink-0 hidden sm:inline">
                  {(b.status || "").replace("_", " ")}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
